import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { COMPARE, SHARED } from "@/constants/testIds";
import {
  Landmark,
  ShieldCheck,
  FileCheck2,
  Handshake,
  Wallet,
  ArrowRight,
  Loader2,
} from "lucide-react";

// Module-scope constant so ReactMarkdown doesn't see a new array reference
// on every render (prevents unnecessary re-renders of the markdown subtree).
const REMARK_PLUGINS = [remarkGfm];

/**
 * Renders the N-column comparison grid + optional advisor takeaway.
 *
 * @param {object} props
 * @param {ComparisonResult} props.result
 * @param {string} [props.advisor]   Rendered markdown for advisor takeaway
 * @param {boolean} [props.advisorStreaming]
 * @param {"editable"|"readonly"} [props.mode]  In readonly mode we omit the deep-link CTAs.
 */
export default function ComparisonGrid({
  result,
  advisor,
  advisorStreaming = false,
  mode = "editable",
}) {
  if (!result) return null;
  const testGrid = mode === "readonly" ? SHARED.grid : COMPARE.grid;
  const testCol = mode === "readonly" ? SHARED.column : COMPARE.column;
  const testAdvisor =
    mode === "readonly" ? SHARED.advisorContent : COMPARE.advisorContent;

  return (
    <div className="space-y-8">
      <div
        data-testid={testGrid}
        className={`grid gap-5 ${
          result.columns.length === 2
            ? "grid-cols-1 md:grid-cols-2"
            : result.columns.length === 3
            ? "grid-cols-1 md:grid-cols-3"
            : "grid-cols-1 md:grid-cols-2 xl:grid-cols-4"
        }`}
      >
        {result.columns.map((col, i) => (
          <div
            key={col.region_id}
            data-testid={testCol(i)}
            className="rounded-2xl border border-border bg-card p-6 flex flex-col"
          >
            <div className="pb-3 border-b border-border">
              <p className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold">
                {col.region_short}
              </p>
              <h3 className="mt-1 font-display text-xl tracking-tight">
                {col.region_name}
              </h3>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                {col.region_blurb}
              </p>
            </div>

            <dl className="mt-4 space-y-3 text-sm">
              <MetricRow label="Rate range" value={col.rate_range} mono />
              <MetricRow label="Tenure envelope" value={col.tenure_range} mono />
              <MetricRow label="Amount envelope" value={col.amount_envelope} mono />
              <MetricRow label="Rate reference" value={col.rate_hint} small />
              <MetricRow
                label="Account identifier"
                value={col.primary_identifier_label}
                small
              />
              <MetricRow
                label="Default currency"
                value={col.region_default_currency}
                mono
              />
            </dl>

            <Group icon={Wallet} title="Payment rails" items={col.top_rails}>
              {col.best_fit_rails?.length > 0 && (
                <div
                  data-testid={`${testCol(i)}-best-rails`}
                  className="mt-2 -mx-1 flex flex-wrap gap-1"
                >
                  <span className="text-[9px] tracking-[0.2em] uppercase font-semibold text-secondary self-center pl-1">
                    Best fit ·
                  </span>
                  {col.best_fit_rails.map((r) => (
                    <span
                      key={r}
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary/10 border border-secondary/30 text-[10px] font-mono text-secondary"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              )}
            </Group>
            <Group icon={ShieldCheck} title="Regulators" items={col.top_regulators} />
            <Group icon={Landmark} title="Compliance" items={col.top_compliance} />
            <Group icon={FileCheck2} title="Key documents" items={col.top_documents} />
            <Group icon={Handshake} title="Guarantee schemes" items={col.top_guarantees} />

            {mode === "editable" ? (
              <Link
                to={`/proposals/new?type=loan&industry=${result.industry_id}&region=${col.region_id}`}
                className="mt-6 pt-4 border-t border-border inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80"
              >
                Generate full proposal
                <ArrowRight aria-hidden="true" className="w-4 h-4" />
              </Link>
            ) : null}
          </div>
        ))}
      </div>

      {advisor ? (
        <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
          <p className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold">
            Advisor takeaway
          </p>
          <div
            data-testid={testAdvisor}
            className="imali-prose mt-3 max-w-none"
          >
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{advisor}</ReactMarkdown>
            {advisorStreaming && (
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 aria-hidden="true" className="w-3 h-3 animate-spin" />
                Streaming…
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MetricRow({ label, value, mono, small }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground shrink-0">
        {label}
      </dt>
      <dd
        className={`text-right ${mono ? "font-mono" : ""} ${
          small ? "text-xs text-muted-foreground" : "text-sm"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function Group({ icon: Icon, title, items, children }) {
  if (!items?.length) return null;
  return (
    <div className="mt-5 border-t border-border pt-4">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
        <Icon aria-hidden="true" className="w-3.5 h-3.5 text-primary" />
        {title}
      </div>
      <ul className="mt-2 space-y-1 text-xs text-foreground/85 leading-relaxed">
        {items.map((it) => (
          <li key={it} className="pl-3 -indent-3">
            <span className="text-secondary mr-1">·</span>
            {it}
          </li>
        ))}
      </ul>
      {children}
    </div>
  );
}
